import React from "react";
import { SiHubspot, SiZoho, SiOkta, SiZapier, SiOpenai } from "react-icons/si";
import { FaMicrosoft } from "react-icons/fa";
import { TbStack2, TbServerCog, TbShieldLock, TbBriefcase } from "react-icons/tb";

// The integration catalogue.
//
// One entry per connector, and the `status` on each is the whole point of this file:
// the screen is a directory of what Pipsqueak can talk to, and a directory that lists
// things it cannot talk to is worse than no directory at all. So status is a fact about
// the code, not a roadmap position:
//
//   available — a real connector a tenant admin can configure here, today.
//   platform  — real and working, but configured by the platform administrator for the
//               whole install rather than per workspace. Listed so nobody goes looking
//               for a switch that is deliberately not here.
//   planned   — nothing is built. No Connect button, because a button that does nothing
//               is a promise the product does not keep.
//
// Moving something to `available` means giving it a `panel` and writing that panel. It
// should not be possible to change this one word and have a card start claiming more
// than the backend can do.

export const CATEGORIES = [
  {
    id: "lead-capture",
    name: "Attack surface lead magnet",
    blurb: "Where a captured lead goes after someone unlocks their report.",
  },
  {
    id: "enrichment",
    name: "Data enrichment",
    blurb: "Pull asset and endpoint detail from an RMM to enrich the scan JSON we already hold.",
  },
  {
    id: "sso",
    name: "Single sign-on",
    blurb: "Let people sign in with the identity provider your organisation already uses.",
  },
  {
    id: "automation",
    name: "Automation",
    blurb: "Hand events to the thousands of apps you already automate against.",
  },
  {
    id: "ai",
    name: "AI",
    blurb: "The model the AI report builder writes with.",
  },
];

export const INTEGRATIONS = [
  // ── Lead capture ──────────────────────────────────────────────────────────
  {
    id: "gohighlevel",
    name: "GoHighLevel",
    category: "lead-capture",
    status: "available",
    panel: "ghl",
    brand: "#2dd4bf",
    icon: TbStack2,
    description: "Sync captured leads as contacts and create an opportunity in your pipeline.",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    category: "lead-capture",
    status: "available",
    panel: "hubspot",
    brand: "#ff7a59",
    icon: SiHubspot,
    description: "Create or update a HubSpot contact and deal when a lead unlocks their report.",
    // Runs alongside GoHighLevel rather than instead of it: lead capture fans out to every
    // enabled destination, so a workspace on both CRMs gets the lead in both.
  },
  {
    id: "zoho",
    name: "Zoho CRM",
    category: "lead-capture",
    status: "available",
    panel: "zoho",
    brand: "#e42527",
    icon: SiZoho,
    description: "Push captured leads into Zoho CRM as leads, or as contacts with a deal.",
    // The description hedges on purpose: Zoho treats leads and contacts as two stages of
    // one person, so the panel makes a workspace pick one rather than writing both.
    note: "Connected with a Zoho Self Client rather than an API key.",
  },

  // {
  //   id: "zomentum",
  //   name: "Zomentum",
  //   category: "lead-capture",
  //   status: "available",
  //   panel: "zomentum",
  //   brand: "#4338ca",
  //   icon: TbBriefcase,
  //   description: "Create the client, contact and opportunity in Zomentum when a lead unlocks their report.",
  //   // Zomentum has no lead record: a contact must belong to a client company, so a capture
  //   // matches or creates the client first. The panel explains that before it happens.
  //   note: "Needs an API user, which is a Growth-plan feature.",
  // },

  // ── Data enrichment ───────────────────────────────────────────────────────
  {
    id: "ninjaone",
    name: "NinjaOne",
    category: "enrichment",
    status: "available",
    panel: "ninjaone",
    brand: "#1c7ff2",
    icon: TbServerCog,
    description: "Match scanned hosts to NinjaOne devices and pull their OS and asset detail.",
  },
  {
    id: "atera",
    name: "Atera",
    category: "enrichment",
    status: "available",
    panel: "atera",
    brand: "#0f62fe",
    icon: TbServerCog,
    description: "Match scanned hosts to Atera agents and pull their asset and patch detail.",
  },
  {
    id: "syncro",
    name: "Syncro",
    category: "enrichment",
    status: "available",
    panel: "syncro",
    brand: "#00b8a9",
    icon: TbServerCog,
    description: "Match scanned hosts to Syncro assets and pull their OS and hardware detail.",
  },
  {
    id: "freshservice",
    name: "Freshservice",
    category: "enrichment",
    status: "available",
    panel: "freshservice",
    brand: "#09a066",
    icon: TbServerCog,
    description: "Match scanned hosts to Freshservice assets and pull their OS and hardware detail.",
  },

  // {
  //   id: "microsoft-365",
  //   name: "Microsoft 365",
  //   category: "enrichment",
  //   status: "available",
  //   panel: "m365",
  //   brand: "#0078d4",
  //   icon: FaMicrosoft,
  //   description: "Pull users, devices, licences and service health from Microsoft Graph.",
  //   note: "A separate app registration from Sign in with Microsoft.",
  // },

  // ── SSO ───────────────────────────────────────────────────────────────────
  {
    id: "entra",
    name: "Microsoft Entra ID",
    category: "sso",
    status: "platform",
    brand: "#0078d4",
    icon: FaMicrosoft,
    description: "Sign in with Microsoft. Already live — it is the Microsoft button on the sign-in page.",
    // routes/entraSso.js reads platform_sso_settings WHERE id = 1: one configuration for
    // the whole install, edited in the Global Admin Dashboard. There is deliberately no
    // per-workspace version of it.
    //
    // Kept separate from the Microsoft 365 connector below even though both are Entra and
    // both are Microsoft. They are different app registrations with different consent:
    // this one asks only who you are, that one reads your directory. One card doing both
    // would mean granting directory-read permissions to sign in.
    note: "Configured by your platform administrator, for every workspace at once.",
  },
  // {
  //   id: "okta",
  //   name: "Okta",
  //   category: "sso",
  //   status: "planned",
  //   brand: "#007dc1",
  //   icon: SiOkta,
  //   description: "Sign in with Okta via OIDC, alongside the existing Microsoft option.",
  // },

  // ── Automation ────────────────────────────────────────────────────────────
  // {
  //   id: "zapier",
  //   name: "Zapier",
  //   category: "automation",
  //   status: "planned",
  //   brand: "#ff4f00",
  //   icon: SiZapier,
  //   description: "Trigger a Zap when a scan completes or a lead is captured, and reach everything else.",
  // },

  // ── AI ────────────────────────────────────────────────────────────────────
  {
    id: "openai",
    name: "OpenAI",
    category: "ai",
    status: "available",
    panel: "ai",
    brand: "#10a37f",
    icon: SiOpenai,
    description: "The API key the AI report builder uses, shared across this workspace.",
  },
];

export const STATUS_LABEL = {
  available: "Available",
  platform: "Managed by platform",
  planned: "Planned",
};

// A single place to ask "can this be opened?", so the card, the button and the detail
// route cannot disagree about it.
export const isManageable = (item) => item.status === "available" && Boolean(item.panel);

export const findIntegration = (id) => INTEGRATIONS.find((i) => i.id === id) || null;

// Icon plus its brand colour, drawn the same way everywhere it appears.
export const IntegrationMark = ({ item, size = 20 }) => {
  const Icon = item.icon || TbShieldLock;
  return (
    <span className="v2-intg-mark" style={{ "--intg-brand": item.brand }}>
      <Icon size={size} />
    </span>
  );
};
